
import Vue from "vue";
import gql from "graphql-tag";
import { callApi } from "@/lib/api";
import crypto from "crypto";
import TextField from "@/components/molecules/TextField.vue";
import PremiumInformation from "@/components/atoms/PremiumInformation.vue";
import { Plan } from "@/lib/models";

export default Vue.extend<Data, Methods, Computed, Props>({
  components: { TextField, PremiumInformation },
  props: {
    novelId: String,
    value: Object,
  },
  created() {
    if (!this.value) {
      return;
    }
    this.name = this.value.name;
    const { detail } = this.value as { detail: any[] };
    if (!detail) {
      return;
    }
    this.detail = this.detail.map((x) => {
      let targetValue = x.value;
      const target = detail.find((y) => y.name === x.name);
      if (target) targetValue = target.value;
      return { ...x, value: targetValue };
    });
    const { additionalColumn } = this.value as { additionalColumn: any[] };
    if (!additionalColumn || additionalColumn.length === 0) {
      return;
    }
    this.additionalColumn = additionalColumn;
  },
  async mounted() {
    const { billingInfo } = await callApi(gql`
      query BillingInfo {
        billingInfo {
          plan
        }
      }
    `);
    this.plan = billingInfo.plan;
  },
  methods: {
    change() {
      const { name, detail, additionalColumn } = this;
      this.$emit("input", {
        name,
        detail: detail.filter(({ value }) => value).map(({ name, value }) => ({ name, value })),
        additionalColumn,
      });
      this.setModified(true);
    },
    add() {
      const { additionalColumn } = this;
      additionalColumn.push({
        name: this.generateUniqueValue(),
        label: "",
        content: "",
      });
      this.change();
    },
    remove(index) {
      const { additionalColumn } = this;
      additionalColumn.splice(index, 1);
      this.change();
    },
    generateUniqueValue() {
      const N = 16;
      return crypto.randomBytes(N).toString("base64").substring(0, N);
    },
  },
  data() {
    return {
      plan: 0,
      name: "",
      detail: [
        {
          name: "alias",
          value: "",
          _label: this.$t('createCharacterPage.aliasLabel'),
          _placeholder: this.$t('createCharacterPage.aliasPlaceholder'),
        },
        {
          name: "role",
          value: "",
          _label: this.$t('createCharacterPage.roleLabel'),
          _placeholder: this.$t('createCharacterPage.rolePlaceholder'),
        },
        {
          name: "gender",
          value: "",
          _label: this.$t('createCharacterPage.genderLabel'),
          _placeholder: this.$t('createCharacterPage.genderPlaceholder'),
        },
        {
          name: "age",
          value: "",
          _label: this.$t('createCharacterPage.ageLabel'),
          _placeholder: this.$t('createCharacterPage.agePlaceholder'),
        },
        {
          name: "birthdate",
          value: "",
          _label: this.$t('createCharacterPage.birthdateLabel'),
          _placeholder: this.$t('createCharacterPage.birthdatePlaceholder'),
        },
        {
          name: "bloodType",
          value: "",
          _label: this.$t('createCharacterPage.bloodTypeLabel'),
          _placeholder: this.$t('createCharacterPage.bloodTypePlaceholder'),
        },
        {
          name: "height",
          value: "",
          _placeholder: this.$t('createCharacterPage.heightPlaceholder'),
          _label: this.$t('createCharacterPage.heightLabel'),
        },
        {
          name: "weight",
          value: "",
          _placeholder: this.$t('createCharacterPage.weightPlaceholder'),
          _label: this.$t('createCharacterPage.weightLabel'),
        },
        {
          name: "personality",
          value: "",
          _label: this.$t('createCharacterPage.personalityLabel'),
          _rows: 4,
          _placeholder: this.$t('createCharacterPage.personalityPlaceholder'),
        },
        {
          name: "individuality",
          value: "",
          _label: this.$t('createCharacterPage.individualityLabel'),
          _rows: 4,
          _placeholder: this.$t('createCharacterPage.individualityPlaceholder'),
        },
        {
          name: "skill",
          value: "",
          _label: this.$t('createCharacterPage.skillLabel'),
          _rows: 4,
          _placeholder: this.$t('createCharacterPage.skillPlaceholder'),
        },
        {
          name: "ability",
          value: "",
          _label: this.$t('createCharacterPage.abilityLabel'),
          _rows: 4,
          _placeholder: this.$t('createCharacterPage.abilityPlaceholder'),
        },
        {
          name: "looks",
          value: "",
          _label: this.$t('createCharacterPage.looksLabel'),
          _rows: 4,
          _placeholder: this.$t('createCharacterPage.looksPlaceholder'),
        },
        {
          name: "personalHistory",
          value: "",
          _label: this.$t('createCharacterPage.personalHistoryLabel'),
          _rows: 4,
          _placeholder: this.$t('createCharacterPage.personalHistoryPlaceholder'),
        },
        {
          name: "background",
          value: "",
          _label: this.$t('createCharacterPage.backgroundLabel'),
          _rows: 4,
          _placeholder: this.$t('createCharacterPage.backgroundPlaceholder'),
        },
        {
          name: "other",
          value: "",
          _label: this.$t('createCharacterPage.otherLabel'),
          _rows: 4,
          _placeholder: this.$t('createCharacterPage.otherPlaceholder'),
        },
      ],
      additionalColumn: [
        {
          name: (this as any).generateUniqueValue(),
          label: "",
          content: "",
        },
      ],
    };
  },
});

interface Props {
  novelId: string;
  value: {
    name: string;
    detail: any[];
    additionalColumn: any[];
  };
}

interface Data {
  plan: Plan;
  name: string;
  detail: any[];
  additionalColumn: any[];
}

interface Computed {}

interface Methods {
  change(): void;
  add(): void;
  remove(index: number): void;
  generateUniqueValue(): string;
}
